/**
 * `useBackendVersion` — low-frequency React Query hook that reads the deployed
 * backend build metadata from the public `GET /api/version` endpoint.
 *
 * Public/unauthenticated (skipAuth) so the version indicator renders on the
 * marketing navbar too. Long stale time + a single retry: the deployed sha does
 * not change between reloads, so we avoid hammering the endpoint. On any error
 * (unreachable / 5xx) the query settles as `isError` with `data === undefined`,
 * which the drift logic maps to the neutral 'unknown' state.
 */

import { useQuery } from '@tanstack/react-query'

import { apiService } from '@/platform/api/api.service'
import { queryKeys } from '@/shared/query-keys'

/** Public route serving the deployed backend build metadata. */
export const BACKEND_VERSION_ROUTE = '/api/version'

/** Backend build metadata contract (any field may be the 'unknown' sentinel). */
export interface BackendVersionInfo {
  gitSha: string
  gitShaShort: string
  gitBranch: string
  buildTime: string
  version: string
  environment: string
}

/** 5 minutes — the deployed sha is effectively static between page loads. */
const VERSION_STALE_TIME_MS = 5 * 60 * 1000

/**
 * Fetch the backend build metadata. Returns the standard React Query result;
 * `data` is `BackendVersionInfo | undefined` (undefined while loading or on
 * error). Consumers pass `data` straight into the drift resolver.
 */
export function useBackendVersion() {
  return useQuery({
    queryKey: queryKeys.config.version(),
    queryFn: async () => {
      // Public endpoint — skipAuth so it also works on unauthenticated pages.
      return apiService.get<BackendVersionInfo>(BACKEND_VERSION_ROUTE, undefined, true)
    },
    select: (response) => response.data,
    staleTime: VERSION_STALE_TIME_MS,
    gcTime: VERSION_STALE_TIME_MS,
    retry: 1,
    refetchOnWindowFocus: false,
  })
}
